import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  X,
  Zap,
  ChevronRight,
  MonitorPlay,
  Gamepad2,
  Tv,
  Music,
  LayoutGrid
} from 'lucide-react';
import { getCategories } from '../firebase/categories';
import { Category } from '../types';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories();
      // Filter main categories (no parentId) and sort by order (Hot first)
      setCategories(
        categoriesData
          .filter(c => !c.parentId)
          .sort((a, b) => {
            if (a.name === 'Hot') return -1;
            if (b.name === 'Hot') return 1;
            return (a.order || 999) - (b.order || 999);
          })
      );
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Helper to get icon for category (you might want to store this in DB or map by name)
  const getCategoryIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('cine') || lowerName.includes('pelicula')) return MonitorPlay;
    if (lowerName.includes('juego') || lowerName.includes('game')) return Gamepad2;
    if (lowerName.includes('tv') || lowerName.includes('tele')) return Tv;
    if (lowerName.includes('musica') || lowerName.includes('spotify')) return Music;
    return LayoutGrid;
  };

  return (
    <>
      {/* Backdrop for mobile and desktop when open */}
      <div 
        className={`
          fixed inset-0 bg-black/80 backdrop-blur-sm z-40 transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen w-80 bg-black/95 border-r border-neon-blue/30 z-50
          transition-transform duration-300 ease-out shadow-[0_0_50px_rgba(0,255,255,0.1)]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-white/10 relative overflow-hidden">
          <div className="flex items-center gap-3">
            <Zap className="text-neon-blue w-8 h-8 animate-pulse" />
            <h1 className="font-orbitron text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-neon-blue to-neon-purple">
              BIGOTES
            </h1>
          </div>
          
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8 overflow-y-auto h-[calc(100vh-80px)]">
          
          {/* Main Navigation */}
          <div>
            <h3 className="text-xs font-orbitron text-gray-500 uppercase tracking-widest mb-4">
              Navegación
            </h3>
            <Link
              to="/"
              onClick={onClose}
              className={`
                flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 group
                ${isActive('/') 
                  ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/30' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
                }
              `}
            >
              <Home className="w-5 h-5" />
              <span className="font-orbitron font-bold text-lg tracking-wide">Inicio</span>
              <ChevronRight className={`ml-auto w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${isActive('/') ? 'text-neon-blue' : ''}`} />
            </Link>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-xs font-orbitron text-gray-500 uppercase tracking-widest mb-4">
              Categorías
            </h3>
            <div className="space-y-2">
              {loading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-white/5 rounded-xl" />
                  ))}
                </div>
              ) : (
                categories.map((category) => {
                  const Icon = getCategoryIcon(category.name);
                  const path = `/categoria/${category.name.toLowerCase()}`;
                  
                  return (
                    <Link
                      key={category.id}
                      to={path}
                      onClick={onClose}
                      className={`
                        flex items-center gap-4 px-4 py-3 rounded-xl transition-all duration-300 group
                        ${isActive(path)
                          ? 'bg-neon-purple/10 text-neon-purple border border-neon-purple/30'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-orbitron font-bold text-lg tracking-wide">{category.name}</span>
                      <ChevronRight className={`ml-auto w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity ${isActive(path) ? 'text-neon-purple' : ''}`} />
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
